import { partialRight, round } from 'lodash';
import type { Dimension, Orientation, Point } from 'types/common_types';

type OrientationAndRatio = {
  orientation: Orientation;
  ratioPercentage: number;
};

export type FeetInchesPair = [feet: number, inches: number];

// How large is the wall preview image when rendered on the background
export function calculateWallPreviewImageSize(
  screenDimensions: Dimension,
  imageDimensions: Dimension
) {
  if (
    !screenDimensions.height ||
    !screenDimensions.width ||
    !imageDimensions.height ||
    !imageDimensions.width
  )
    return { height: 0, width: 0 };

  const sar = screenDimensions.width / screenDimensions.height;
  const iar = imageDimensions.width / imageDimensions.height;

  // If screen is wider than the image, constrain the image by the height of the screen
  const constraint: keyof Dimension = sar > iar ? 'height' : 'width';
  const other: keyof Dimension = constraint === 'width' ? 'height' : 'width';

  // If we're constraining by the height (because screen is wider), we use the image's original aspect ratio
  // to determine the image's width
  // If we're contstraining by width, we need to use the inverse to determine the image's height
  const ratioAdjustment = sar > iar ? iar : 1 / iar;

  return {
    [constraint]: screenDimensions[constraint],
    [other]: screenDimensions[constraint] * ratioAdjustment,
  } as Dimension;
}

export function centerWithinContainer(toCenter: Dimension, container: Dimension): Point {
  return {
    x: container.width / 2 - toCenter.width / 2,
    y: container.height / 2 - toCenter.height / 2,
  };
}

export function orientationAndRatioPercentage(width: number, height: number) {
  let orientation: Orientation;
  let ratioPercentage: number;

  const { nom, dom } = aspectRatio(width, height);

  if (nom > dom) {
    orientation = 'landscape';
    ratioPercentage = dom / nom; // small first to produce a %
  } else if (nom === dom) {
    orientation = 'square';
    ratioPercentage = dom / nom; // doesn't matter which first
  } else {
    orientation = 'portrait';
    ratioPercentage = nom / dom; // small first to produce a %
  }

  return { orientation, ratioPercentage } as OrientationAndRatio;
}

export const roundToHalf = partialRight(roundvalue, 0.5);
export const roundToQuarter = partialRight(roundvalue, 0.25);

// TODO: should probably add a precision arg to this as well
export function roundvalue(value: number, increment: number | string) {
  let rounded = value;
  const remainder = rounded % 1;

  const incrementFloat = typeof increment === 'string' ? parseFloat(increment) : increment;

  if (remainder) {
    const base = Math.floor(value);
    let fraction = incrementFloat;
    while (fraction % 1 && fraction < remainder) {
      fraction += incrementFloat;
    }
    const ceil = fraction;
    const floor = fraction - incrementFloat;
    const addition = ceil - remainder > remainder - floor ? floor : ceil;
    rounded = base + addition;
  }

  return rounded;
}

export function inchesToFeetInches(inches: number): FeetInchesPair {
  let ft = Math.floor(inches / 12);
  let inch = round(inches % 12);

  // round 12 inches up to an even foot
  if (inch === 12) {
    inch = 0;
    ft += 1;
  }

  return [ft, inch];
}

export function realNumber(n: number) {
  return !Number.isNaN(n) && Number.isFinite(n);
}

function gcd(a: number, b: number): number {
  if (!b) {
    return a;
  }
  return gcd(b, a % b);
}

function aspectRatio(w: number, h: number) {
  const r = gcd(w, h);

  return {
    nom: w / r,
    dom: h / r,
  };
}

/** Find the inverse of a percent-cropped dimension -- crop must not be zero */
export function invertCroppedDimension(dimension: number, crop: number) {
  if (crop === 0) return 0;

  return dimension * (1 / crop);
}
